import React, { useState, useEffect } from 'react';
import styles from './MapViewModal.module.css';
import iconClose from '../../../images/icon-close.svg';

interface MapViewModalProps {
    isOpen: boolean;
    onClose: () => void;
    propertyName: string;
    fileUrl: string | null;
    fileType: 'image' | 'pdf';
}


const MapViewModal: React.FC<MapViewModalProps> = ({
    isOpen,
    onClose,
    propertyName,
    fileUrl,
    fileType
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Reset loading state when modal opens
    useEffect(() => {
        if (isOpen) {
            setIsLoading(true);
            setError(null);
        }
    }, [isOpen]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            // Prevent scrolling on body and html
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
            // Prevent scroll on mobile devices
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
        } else {
            // Restore scrolling
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        }

        return () => {
            // Cleanup on unmount
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        };
    }, [isOpen]);

    // Handle backdrop click to close modal
    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Prevent scroll on overlay (including touch devices)
    const handleOverlayTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            e.preventDefault();
        }
    };

    // Handle file load success
    const handleFileLoad = () => {
        setIsLoading(false);
    };

    // Handle file load error
    const handleFileError = () => {
        setIsLoading(false);
        setError('Failed to load file. Please try again.');
    };

    if (!isOpen) return null;

    return (
        <div
            className={styles.modalOverlay}
            onClick={handleBackdropClick}
            onTouchMove={handleOverlayTouchMove}
        >
            <div className={styles.modalContainer}>
                {/* Modal Header */}
                <div className={styles.modalHeader}>
                    <div className={styles.headerContent}>
                        <h2 className={styles.propertyName}>{propertyName}</h2>

                    </div>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        <img src={iconClose} alt="Close" />
                    </button>
                </div>

                {/* Modal Body */}
                <div className={styles.modalBody}>
                    {isLoading && (
                        <div className={styles.loadingContainer}>
                            <div className={styles.spinner}></div>
                            <p>Loading...</p>
                        </div>
                    )}

                    {error && (
                        <div className={styles.errorMessage}>{error}</div>
                    )}

                    {fileUrl && !error && (
                        fileType === 'pdf' ? (
                            <iframe
                                src={fileUrl}
                                className={styles.pdfViewer}
                                onLoad={handleFileLoad}
                                onError={handleFileError}
                                title={`${propertyName} Map`}
                            />
                        ) : (
                            <img
                                src={fileUrl}
                                alt={`${propertyName} Map`}
                                className={styles.imageViewer}
                                onLoad={handleFileLoad}
                                onError={handleFileError}
                            />
                        )
                    )}
                </div>

            </div>
        </div>
    );
};

export default MapViewModal;

